import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add,
  Remove,
  InfoOutlined,
  RestartAlt,
  PictureAsPdf,
  CheckCircle,
} from '@mui/icons-material';
import {
  CalculatorInputs,
  BedCounts,
  QualityKey,
  LaundryMethod,
  RotationLevel,
  QuoteResult,
  QualityOption,
  fetchLinenQuote,
  fetchQualityOptions,
  formatCurrency,
  defaultInputs,
  fallbackQualityOptions,
} from '../../utils/linenPricing';

const BED_LABELS: { key: keyof BedCounts; label: string }[] = [
  { key: 'king', label: 'King Beds' },
  { key: 'queen', label: 'Queen Beds' },
  { key: 'full', label: 'Full / Double Beds' },
  { key: 'twin', label: 'Twin Beds' },
  { key: 'sofa', label: 'Sofa Beds' },
];

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));

const LinenCalculator: React.FC = () => {
  const theme = useTheme();
  const [inputs, setInputs] = useState<CalculatorInputs>(defaultInputs);
  const [quote, setQuote] = useState<QuoteResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [qualityOptions, setQualityOptions] = useState<QualityOption[]>(fallbackQualityOptions);

  const totalBeds = Object.values(inputs.beds).reduce((a, b) => a + b, 0);
  const hasBeds = totalBeds > 0;
  const qualityLabel =
    quote?.qualityLabel ||
    qualityOptions.find((o) => o.key === inputs.quality)?.label ||
    inputs.quality;

  // Display options (labels + examples only — pricing stays server-side)
  useEffect(() => {
    fetchQualityOptions().then(setQualityOptions).catch(() => {
      /* fall back to the static labels */
    });
  }, []);

  // Debounced server-side pricing: the config (margins, fees, reserves)
  // never ships to the browser.
  useEffect(() => {
    if (!hasBeds) {
      setQuote(null);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError('');
    const timer = setTimeout(() => {
      fetchLinenQuote(inputs, controller.signal)
        .then((result) => {
          setQuote(result);
          setLoading(false);
        })
        .catch((err: Error) => {
          if (err.name === 'AbortError') return;
          setError(err.message || 'Could not reach the pricing service.');
          setLoading(false);
        });
    }, 300);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [inputs, hasBeds]);

  const update = (patch: Partial<CalculatorInputs>) =>
    setInputs((prev) => ({ ...prev, ...patch }));

  const updateBed = (key: keyof BedCounts, delta: number) =>
    setInputs((prev) => ({
      ...prev,
      beds: { ...prev.beds, [key]: clamp(prev.beds[key] + delta, 0, 20) },
    }));

  const handleReset = () => setInputs(defaultInputs);

  const handleExport = () => {
    if (!hasBeds || !quote) return;
    const rows = (label: string, value: string) =>
      `<tr><td>${label}</td><td style="text-align:right;font-weight:600">${value}</td></tr>`;
    const row3 = (label: string, owner: string, program: string) =>
      `<tr><td>${label}</td><td style="text-align:right;font-weight:600">${owner}</td><td style="text-align:right;font-weight:700;color:#2e7d32">${program}</td></tr>`;

    const win = window.open('', '_blank', 'width=800,height=900');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>Hospitality Linen Program — Quote</title>
      <style>
        body { font-family: 'Segoe UI', Arial, sans-serif; color: #191919; max-width: 640px; margin: 40px auto; padding: 0 24px; }
        h1 { font-size: 22px; margin-bottom: 0; }
        .sub { color: #666; margin-top: 4px; }
        table { width: 100%; border-collapse: collapse; margin: 18px 0; }
        td, th { padding: 8px 4px; border-bottom: 1px solid #e5e5e5; font-size: 14px; text-align: left; }
        .totals td { font-size: 16px; font-weight: 700; border-bottom: none; }
        .disclaimer { color: #888; font-size: 12px; margin-top: 24px; }
      </style></head><body>
      <h1>5 Fairies — Hospitality Linen Program</h1>
      <div class="sub">Estimate prepared ${new Date().toLocaleDateString('en-US')}</div>
      <table>
        <tr><th colspan="2">Property</th></tr>
        ${rows('Hospitality quality', qualityLabel)}
        ${rows('Bedrooms', String(inputs.bedrooms))}
        ${rows('Beds', String(quote.totalBeds))}
        ${rows('Bathrooms', String(inputs.bathrooms))}
        ${rows('Maximum occupancy', String(inputs.maxOccupancy))}
        ${rows('Linen rotations', String(inputs.rotations))}
        ${rows('Laundry method', inputs.laundryMethod === 'onsite' ? 'On-Site Linen Management' : 'Off-Site Linen Exchange')}
        ${rows('Turnovers per month', String(inputs.turnoversPerMonth))}
        ${rows('Laundry complexity', quote.complexityLabel)}
      </table>
      <table>
        <tr><th>Upfront inventory cost you avoid</th><th style="text-align:right">You buy</th><th style="text-align:right;color:#2e7d32">5Fairies Hospitality Program</th></tr>
        ${row3('Bed linens', formatCurrency(quote.inventoryBreakdown.beds), 'Included')}
        ${row3('Bathroom & guest towels', formatCurrency(quote.inventoryBreakdown.bathrooms), 'Included')}
        ${quote.inventoryBreakdown.optionalTowels > 0 ? row3('Optional towels', formatCurrency(quote.inventoryBreakdown.optionalTowels), 'Included') : ''}
        <tr class="totals"><td>Estimated inventory value</td><td style="text-align:right">${formatCurrency(quote.inventoryValue)}</td><td style="text-align:right;color:#2e7d32">$0</td></tr>
      </table>
      <table>
        <tr><th colspan="2">Pricing</th></tr>
        ${rows('Hospitality Linen Program', `${formatCurrency(quote.monthlyPrice)}/month`)}
        ${quote.setupFee > 0 ? rows('One-time setup fee', formatCurrency(quote.setupFee)) : ''}
        ${rows('Linen Management', `${formatCurrency(quote.turnoverFee)} per turnover`)}
        <tr class="totals"><td>Estimated Monthly Cost</td><td style="text-align:right">${formatCurrency(quote.monthlyTotal)}</td></tr>
        <tr class="totals"><td>Estimated Annual Cost</td><td style="text-align:right">${formatCurrency(quote.annualTotal)}</td></tr>
      </table>
      ${quote.setupFee > 0 ? '<p class="disclaimer">Setup Fee = One-Time Operational Costs + Customer Contribution to Inventory</p>' : ''}
      <table>
        <tr><th>Linen configuration — bedrooms</th><th style="text-align:right">Qty</th></tr>
        ${quote.linenConfiguration.bedrooms.map((i) => rows(i.item, String(i.quantity))).join('')}
      </table>
      <table>
        <tr><th>Linen configuration — bathrooms</th><th style="text-align:right">Qty</th></tr>
        ${quote.linenConfiguration.bathrooms.map((i) => rows(i.item, String(i.quantity))).join('')}
      </table>
      <div><strong>Estimated upfront investment avoided: ${formatCurrency(quote.upfrontInvestmentAvoided)}</strong></div>
      <p class="disclaimer">This estimate is based on standard hospitality inventory assumptions. Final pricing may vary after reviewing your property's specific linen requirements.</p>
      <script>window.onload = function () { window.print(); };</script>
      </body></html>`);
    win.document.close();
  };

  const showValues = hasBeds && quote !== null;

  const pricingCards = [
    {
      label: 'Hospitality Linen Program',
      value: quote ? formatCurrency(quote.monthlyPrice) : '',
      unit: '/month',
      note: quote && quote.setupFee > 0 ? `+ ${formatCurrency(quote.setupFee)} one-time setup` : null,
    },
    { label: 'Linen Management', value: quote ? formatCurrency(quote.turnoverFee) : '', unit: ' per turnover' },
    { label: 'Estimated Monthly Cost', value: quote ? formatCurrency(quote.monthlyTotal) : '', unit: '' },
    { label: 'Estimated Annual Cost', value: quote ? formatCurrency(quote.annualTotal) : '', unit: '' },
  ];

  const b = quote?.inventoryBreakdown;
  // [label, value, upfront cost owner would pay, program-column cell]
  const summaryRows: [string, string, string | null, string | null][] = [
    ['Quality', qualityLabel, null, null],
    ['Bedrooms', String(inputs.bedrooms), null, null],
    ['Beds', String(totalBeds), b ? formatCurrency(b.beds) : '—', 'Included'],
    // Bathroom upfront cost includes per-guest towel sets, so it grows with occupancy
    ['Bathrooms & Guest Towels', String(inputs.bathrooms), b ? formatCurrency(b.bathrooms) : '—', 'Included'],
    [
      'Maximum Occupancy',
      quote && quote.extraGuests > 0
        ? `${inputs.maxOccupancy} (${quote.extraGuests} above the ${quote.includedOccupancy} included)`
        : String(inputs.maxOccupancy),
      null,
      null,
    ],
    ['Rotation Level', `${inputs.rotations} rotations`, null, null],
    ['Laundry Method', inputs.laundryMethod === 'onsite' ? 'On-Site Linen Management' : 'Off-Site Linen Exchange', null, null],
    ...(inputs.poolTowels + inputs.beachTowels > 0
      ? [[
          'Optional Towels',
          `${inputs.poolTowels + inputs.beachTowels}`,
          b ? formatCurrency(b.optionalTowels) : '—',
          'Included',
        ] as [string, string, string | null, string | null]]
      : []),
    ['Laundry Complexity', quote ? quote.complexityLabel : '—', null, null],
    ['Estimated Inventory Value', '', quote ? formatCurrency(quote.inventoryValue) : '—', '$0'],
  ];

  const comparisonRows: [string, string][] = [
    ['Upfront linen purchase required', 'No upfront linen purchase'],
    ['Owner replaces damaged inventory', 'Normal wear replacement included'],
    ['Owner manages inventory', 'Inventory managed by 5 Fairies'],
    ['Variable replacement costs', 'Predictable monthly pricing'],
  ];

  return (
    <Paper elevation={2} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
      <Grid container spacing={4}>
        {/* ------------------------------ Inputs ------------------------------ */}
        <Grid item xs={12} md={5}>
          <Stack spacing={3}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 700 }}>Hospitality Quality</FormLabel>
              <RadioGroup
                value={inputs.quality}
                onChange={(e) => update({ quality: e.target.value as QualityKey })}
              >
                {qualityOptions.map((option) => (
                  <FormControlLabel
                    key={option.key}
                    value={option.key}
                    control={<Radio />}
                    sx={{ alignItems: 'flex-start', mt: 1 }}
                    label={
                      <Box>
                        <Typography fontWeight={600}>
                          {option.label}{option.key === 'midscale' ? ' (Recommended)' : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.examples.join(' · ')}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <Box>
              <FormLabel sx={{ fontWeight: 700 }}>Bed Inventory</FormLabel>
              {!hasBeds && (
                <Alert severity="warning" sx={{ mt: 1 }}>At least one bed is required.</Alert>
              )}
              <Stack spacing={1} sx={{ mt: 1 }}>
                {BED_LABELS.map(({ key, label }) => (
                  <Stack key={key} direction="row" alignItems="center" justifyContent="space-between">
                    <Typography>{label}</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <IconButton
                        size="small"
                        aria-label={`Decrease ${label}`}
                        onClick={() => updateBed(key, -1)}
                        disabled={inputs.beds[key] <= 0}
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                      <Typography sx={{ width: 24, textAlign: 'center', fontWeight: 700 }} aria-live="polite">
                        {inputs.beds[key]}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label={`Increase ${label}`}
                        onClick={() => updateBed(key, 1)}
                        disabled={inputs.beds[key] >= 20}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Bedrooms"
                  value={inputs.bedrooms}
                  onChange={(e) => update({ bedrooms: clamp(parseInt(e.target.value, 10), 1, 15) })}
                  inputProps={{ min: 1, max: 15, 'aria-label': 'Bedrooms (1 to 15)' }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Bathrooms"
                  value={inputs.bathrooms}
                  onChange={(e) => update({ bathrooms: clamp(parseInt(e.target.value, 10), 1, 15) })}
                  inputProps={{ min: 1, max: 15, 'aria-label': 'Bathrooms (1 to 15)' }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Occupancy"
                  value={inputs.maxOccupancy}
                  onChange={(e) => update({ maxOccupancy: clamp(parseInt(e.target.value, 10), 1, 30) })}
                  inputProps={{ min: 1, max: 30, 'aria-label': 'Maximum occupancy (1 to 30)' }}
                />
              </Grid>
            </Grid>

            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 700 }}>
                Linen Rotation Level
                <Tooltip title="A rotation is one complete set of linens and towels required to prepare the property for one guest stay.">
                  <IconButton size="small" aria-label="What is a rotation?">
                    <InfoOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </FormLabel>
              <RadioGroup
                row
                value={String(inputs.rotations)}
                onChange={(e) => update({ rotations: parseInt(e.target.value, 10) as RotationLevel })}
              >
                <FormControlLabel value="2" control={<Radio />} label="2 Rotations" />
                <FormControlLabel value="3" control={<Radio />} label="3 Rotations (Recommended)" />
                <FormControlLabel value="4" control={<Radio />} label="4 Rotations" />
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 700 }}>Laundry Method</FormLabel>
              <RadioGroup
                value={inputs.laundryMethod}
                onChange={(e) => update({ laundryMethod: e.target.value as LaundryMethod })}
              >
                <FormControlLabel value="onsite" control={<Radio />} label="On-Site Linen Management" />
                <FormControlLabel value="offsite" control={<Radio />} label="Off-Site Linen Exchange" />
              </RadioGroup>
            </FormControl>

            <Box>
              <FormLabel sx={{ fontWeight: 700 }}>
                Expected Turnovers Per Month: {inputs.turnoversPerMonth}
              </FormLabel>
              <Slider
                value={inputs.turnoversPerMonth}
                onChange={(_e, v) => update({ turnoversPerMonth: v as number })}
                min={1}
                max={20}
                marks={[{ value: 1, label: '1' }, { value: 10, label: '10' }, { value: 20, label: '20' }]}
                valueLabelDisplay="auto"
                aria-label="Expected turnovers per month"
              />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Pool Towels (optional)"
                  value={inputs.poolTowels}
                  onChange={(e) => update({ poolTowels: clamp(parseInt(e.target.value, 10) || 0, 0, 50) })}
                  inputProps={{ min: 0, max: 50 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Beach Towels (optional)"
                  value={inputs.beachTowels}
                  onChange={(e) => update({ beachTowels: clamp(parseInt(e.target.value, 10) || 0, 0, 50) })}
                  inputProps={{ min: 0, max: 50 }}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2}>
              <Button variant="outlined" startIcon={<RestartAlt />} onClick={handleReset}>
                Reset
              </Button>
              <Button
                variant="contained"
                startIcon={<PictureAsPdf />}
                onClick={handleExport}
                disabled={!hasBeds || !quote || loading}
              >
                Export My Final Quote
              </Button>
            </Stack>
          </Stack>
        </Grid>

        {/* ------------------------------ Results ------------------------------ */}
        <Grid item xs={12} md={7}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ opacity: loading ? 0.55 : 1, transition: 'opacity 0.2s' }} aria-busy={loading}>
            {pricingCards.map((card, index) => (
              <Grid item xs={6} key={card.label}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    height: '100%',
                    borderRadius: 2,
                    bgcolor: index < 2 ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
                  }}
                >
                  <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block' }}>
                    {card.label}
                  </Typography>
                  <Typography variant="h5" fontWeight={800} color={showValues ? 'text.primary' : 'text.disabled'}>
                    {showValues ? card.value : '—'}
                    {showValues && card.unit && (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {card.unit}
                      </Typography>
                    )}
                  </Typography>
                  {showValues && (card as { note?: string | null }).note ? (
                    <Tooltip title="Setup Fee = One-Time Operational Costs + Customer Contribution to Inventory">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textDecoration: 'underline dotted', cursor: 'help' }}
                      >
                        {(card as { note?: string | null }).note}
                      </Typography>
                    </Tooltip>
                  ) : null}
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Inventory Summary
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Details</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Upfront Cost</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    5Fairies Hospitality Program
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryRows.map(([label, value, upfront, program]) => (
                  <TableRow
                    key={label}
                    sx={label === 'Estimated Inventory Value'
                      ? { bgcolor: alpha(theme.palette.primary.main, 0.04), '& td': { fontWeight: 800 } }
                      : undefined}
                  >
                    <TableCell sx={{ color: 'text.secondary' }}>{label}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{showValues ? value : '—'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {upfront === null ? '—' : showValues ? upfront : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {program === null ? '—' : showValues ? program : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {showValues && quote && (
            <>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Your Linen Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Everything supplied and maintained for your property at{' '}
                {inputs.rotations} rotations.
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {([
                  ['Bedrooms', quote.linenConfiguration.bedrooms],
                  ['Bathrooms', quote.linenConfiguration.bathrooms],
                ] as [string, { item: string; quantity: number }[]][]).map(([group, items]) => (
                  <Grid item xs={12} sm={6} key={group}>
                    <Paper variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                      <Typography
                        variant="overline"
                        sx={{
                          display: 'block',
                          px: 2,
                          py: 1,
                          fontWeight: 700,
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                        }}
                      >
                        {group}
                      </Typography>
                      <Table size="small">
                        <TableBody>
                          {items.length === 0 ? (
                            <TableRow>
                              <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                            </TableRow>
                          ) : (
                            items.map(({ item, quantity }) => (
                              <TableRow key={item}>
                                <TableCell sx={{ color: 'text.secondary' }}>{item}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, width: 64 }}>
                                  {quantity}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Owner Comparison
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                  <TableCell sx={{ fontWeight: 700 }}>Owner-Owned</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Hospitality Linen Program</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonRows.map(([owner, program]) => (
                  <TableRow key={owner}>
                    <TableCell>{owner}</TableCell>
                    <TableCell sx={{ color: 'success.main', fontWeight: 600 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <CheckCircle fontSize="inherit" />
                        <span>{program}</span>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Chip
            color="primary"
            label={
              showValues && quote
                ? `Estimated Upfront Investment Avoided: ${formatCurrency(quote.upfrontInvestmentAvoided)}`
                : 'Add at least one bed to see your estimate'
            }
            sx={{ fontWeight: 700, mb: 2 }}
          />

          <Typography variant="caption" color="text.secondary" display="block">
            This estimate is based on standard hospitality inventory assumptions. Final pricing
            may vary after reviewing your property's specific linen requirements.
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default LinenCalculator;
